import { useEffect, useState } from 'react';
import { useUi } from '../../store/ui.ts';
import { useAuth } from '../../store/auth.ts';
import { fetchBlobUrl, thumbnailBlobUrl } from '../../api/files.ts';
import { formatBytes, formatDateTime, previewKindOf } from '../../lib/format.ts';
import { FileIcon } from './FileIcon.tsx';
import { HistoryTab } from './HistoryTab.tsx';
import { ShareTab } from './ShareTab.tsx';
import { IconClose } from '../../lib/icons.tsx';
import type { ApiNode } from '../../api/types.ts';

interface Props {
  namespaceId: string;
  node: ApiNode;
  onClose: () => void;
}

const ALL_TABS = [
  { key: 'info', label: '정보', feature: null },
  { key: 'history', label: '히스토리', feature: null },
  { key: 'share', label: '공유', feature: 'sharing' },
] as const;

export function DetailsDrawer({ namespaceId, node, onClose }: Props) {
  const sharing = useAuth((s) => s.features.sharing);
  const tabs = ALL_TABS.filter((t) => t.feature === null || sharing);
  const rawTab = useUi((s) => s.drawerTab);
  const tab = tabs.some((t) => t.key === rawTab) ? rawTab : 'info';
  const setTab = useUi((s) => s.setDrawerTab);
  const [thumb, setThumb] = useState<string | null>(null);

  useEffect(() => {
    setThumb(null);
    if (node.type !== 'file') return;
    if (previewKindOf(node.name, node.mime) !== 'image') return;

    // 서버 썸네일(l=1024px)을 우선 쓴다. 원본이 작아 썸네일이 없으면 원본을 받는다.
    let ownedUrl: string | null = null;
    const load = async (): Promise<void> => {
      if (node.has_thumbnail) {
        const result = await thumbnailBlobUrl(namespaceId, node.path, 'l').catch(() => null);
        if (result && result !== 'generating') { setThumb(result); return; }
      }
      if (node.size > 8 * 1024 * 1024) return;   // 썸네일이 없는 대형 원본은 건너뛴다
      const url = await fetchBlobUrl(namespaceId, node.path).catch(() => null);
      if (url) { ownedUrl = url; setThumb(url); }
    };
    void load();
    // 썸네일 캐시의 URL 은 공유 자원이라 해제하지 않는다. 직접 받은 원본만 정리한다.
    return () => { if (ownedUrl) URL.revokeObjectURL(ownedUrl); };
  }, [namespaceId, node.path, node.type, node.name, node.mime, node.size, node.has_thumbnail]);

  return (
    <aside className="drawer" aria-label="상세 정보">
      <div className="drawer__head">
        <FileIcon node={node} />
        <span className="truncate spacer" title={node.name}>{node.name}</span>
        <button className="btn btn--ghost btn--icon" aria-label="닫기" onClick={onClose}>
          <IconClose size={16} />
        </button>
      </div>

      <div className="drawer__tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            className="drawer__tab"
            data-active={tab === t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => { setTab(t.key); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="drawer__body">
        {tab === 'info' && (
          <>
            {thumb && <div className="drawer__preview"><img src={thumb} alt="" /></div>}
            <dl className="kv">
              <dt>종류</dt><dd>{node.type === 'folder' ? '폴더' : node.mime ?? '파일'}</dd>
              <dt>크기</dt><dd>{node.type === 'folder' ? '—' : formatBytes(node.size)}</dd>
              <dt>경로</dt><dd>{node.path}</dd>
              <dt>수정</dt><dd>{formatDateTime(node.modified_at)}</dd>
              <dt>수정자</dt><dd>{node.modified_by?.name ?? '—'}</dd>
              <dt>생성</dt><dd>{formatDateTime(node.created_at)}</dd>
              <dt>리비전</dt><dd className="mono">{node.rev}</dd>
              {node.content_hash && (
                <>
                  <dt>내용 해시</dt>
                  <dd className="mono">{node.content_hash.slice(0, 16)}…</dd>
                </>
              )}
              {node.lock && (
                <>
                  <dt>잠금</dt>
                  <dd>{node.lock.name} · {formatDateTime(node.lock.expires_at)}까지</dd>
                </>
              )}
            </dl>
          </>
        )}
        {tab === 'history' && <HistoryTab namespaceId={namespaceId} node={node} />}
        {tab === 'share' && sharing && <ShareTab namespaceId={namespaceId} node={node} />}
      </div>
    </aside>
  );
}
